import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (item) => {
    if (item.type === 'link') {
      navigate(item.id);
      setIsMobileMenuOpen(false);
    } else {
      // If on gallery page, go home first
      if (location.pathname !== '/') {
        navigate('/', { state: { scrollTo: item.id } });
      } else {
        scrollToSection(item.id);
      }
      setIsMobileMenuOpen(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    // Handle scroll after navigation
    if (location.state?.scrollTo) {
      setTimeout(() => {
        scrollToSection(location.state.scrollTo);
      }, 100);
    }
  }, [location]);

  const navItems = [
    { label: 'Home', id: 'home', type: 'scroll' },
    { label: 'About', id: 'about', type: 'scroll' },
    { label: 'Priorities', id: 'priorities', type: 'scroll' },
    { label: 'Get Involved', id: 'get-involved', type: 'scroll' },
    { label: 'Gallery', id: '/gallery', type: 'link' },
    { label: 'Contact', id: 'contact', type: 'scroll' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo/Brand */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center focus:outline-none group py-2"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_wanda-for-kissimmee/artifacts/aax9kmpd_IMG_7910%20%281%29logo.png"
              alt="Wanda Roman Campaign Logo"
              className="h-16 sm:h-18 md:h-20 lg:h-22 w-auto object-contain transition-transform duration-200 group-hover:scale-105"
            />
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigation(item)}
                className={`font-medium transition-colors duration-300 text-sm xl:text-base ${
                  isScrolled
                    ? 'text-gray-700 hover:text-purple-700'
                    : 'text-white hover:text-amber-300'
                }`}
              >
                {item.label}
              </button>
            ))}
            <a
              href="https://square.link/u/PPzV8yyu"
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center justify-center px-6 py-2 rounded-md transition-all duration-300 font-semibold ${
                isScrolled
                  ? 'bg-purple-700 hover:bg-purple-800 text-white'
                  : 'bg-amber-500 hover:bg-amber-600 text-purple-900 shadow-lg'
              }`}
            >
              Donate
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden transition-colors duration-300 ${
              isScrolled
                ? 'text-gray-700 hover:text-purple-700'
                : 'text-white hover:text-amber-300'
            }`}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className={`lg:hidden pb-4 animate-in slide-in-from-top duration-300 ${
            isScrolled ? 'bg-white/95' : 'bg-purple-900/95 backdrop-blur-md'
          } rounded-b-lg`}>
            <div className="flex flex-col space-y-4 px-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item)}
                  className={`font-medium transition-colors duration-300 text-left py-2 ${
                    isScrolled
                      ? 'text-gray-700 hover:text-purple-700'
                      : 'text-white hover:text-amber-300'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <a
                href="https://square.link/u/PPzV8yyu"
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center justify-center w-full font-semibold transition-all duration-300 px-4 py-2 rounded-md ${
                  isScrolled
                    ? 'bg-purple-700 hover:bg-purple-800 text-white'
                    : 'bg-amber-500 hover:bg-amber-600 text-purple-900'
                }`}
              >
                Donate Now
              </a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
